import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mic, Sparkles, PenLine, FileText, Wrench, Building2, BookOpen, PiggyBank, Volume2, Clock, TrendingDown, Handshake } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { DemoChapters, DemoVertical, DemoShare, demoFile } from "@/components/DemoMedia";
import { useLanguage } from "@/i18n";

const STEPS = [
  [Mic, "Dossier chantier chez le client", "Client, adresse, besoin dicté à la voix : le dossier est ouvert en 30 secondes, sur place."],
  [Sparkles, "Devis chiffré par l'IA", "Postes, quantités et prix depuis votre bibliothèque, fournitures et main-d'œuvre séparées."],
  [FileText, "PDF du devis, preuve à l'appui", "Logo, assurance, conditions : le document est prêt pendant que vous êtes encore chez le client."],
  [PenLine, "Signature sur place, au doigt", "Le client relit le total sur votre téléphone et signe. Nom, heure et IP tracés dans le PDF."],
  [FileText, "Facture en un clic", "Numérotée, conforme, PDF généré ; envoi e-mail ou Peppol, relance et demande d'avis."],
  [Wrench, "Intervention tracée", "Travaux cochés, recommandations, signature du client par lien, rapport PDF."],
  [BookOpen, "Bibliothèque de prix", "Vos prestations, coûts et marges — la base que l'IA utilise pour chiffrer."],
  [Building2, "Bâtiments et rappels d'entretien", "Chaque équipement installé, sa garantie, le prochain entretien rappelé automatiquement, QR code."],
  [PiggyBank, "Finances suivies", "Facturé, encaissé, TVA par trimestre, export comptable."],
];

const HOOK = [
  [Clock, "Le devis envoyé 3 jours après", "Le client a refroidi, comparé, oublié. Le devis par e-mail se perd dans la boîte de réception."],
  [TrendingDown, "Le devis perdu", "La plupart des devis ne se perdent pas sur le prix : ils se perdent parce qu'ils ne sont pas vendus sur place."],
  [Handshake, "Le devis signé chez le client", "Avec Devisto, vous créez, présentez et faites signer le devis pendant que le client est convaincu."],
];

export default function DemoPage() {
  const videoRef = useRef(null);
  const { lang, t } = useLanguage();
  return (
    <div className="font-body min-h-screen bg-[#F3F5FC] text-[#0F2645]">
      <Header />
      <main data-testid="demo-page" className="mx-auto max-w-5xl px-5 py-12 lg:py-16">
        <span className="inline-flex items-center gap-2 rounded-full border border-[#3A5CC8]/30 bg-[#EEF1FC] px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-[#3A5CC8]">{t("Démo complète · 6 min · voix belge")}</span>
        <h1 className="mt-5 font-heading text-4xl font-extrabold tracking-tight text-[#0F2645] sm:text-5xl lg:text-6xl lg:leading-[1.05]">
          {t("Vendez le devis")} <span className="text-[#3A5CC8]">{t("chez le client")}</span>{t(", pas trois jours plus tard")}
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-[#5C6A87] md:text-lg">
          {t("Le parcours complet d'un artisan sur l'application réelle : dossier ouvert sur place, devis dicté et chiffré par l'IA, PDF, signature au doigt, facture, intervention, bibliothèque de prix, suivi des bâtiments et rappels d'entretien.")}
        </p>

        <div className="dv-card-in mt-8 overflow-hidden rounded-3xl border border-[#E2E7F7] bg-[#08104D] shadow-[0_30px_80px_-30px_rgba(8,16,77,0.6)]">
          <video
            key={lang}
            ref={videoRef}
            data-testid="demo-video"
            controls
            playsInline
            preload="metadata"
            poster={demoFile("devisto-demo", lang, "-poster.jpg")}
            className="aspect-video w-full"
          >
            <source src={demoFile("devisto-demo", lang, ".mp4")} type="video/mp4" />
            <source src={demoFile("devisto-demo", lang, ".webm")} type="video/webm" />
            {t("Votre navigateur ne peut pas lire cette vidéo.")}
          </video>
        </div>
        <p data-testid="demo-video-note" className="mt-3 flex items-start gap-2 text-xs text-[#93A0BC]"><Volume2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#3A5CC8]" /> {t("Vidéo enregistrée sur l'application réelle, racontée par une voix belge native, sous-titrée. Activez le son.")}</p>
        <DemoChapters videoRef={videoRef} />
        <DemoShare />

        <section data-testid="demo-hook" className="mt-14 rounded-3xl bg-[#08104D] p-7 text-white sm:p-10">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#9FB4F2]">{t("Pourquoi les devis se perdent")}</p>
          <h2 className="mt-3 font-heading text-base font-bold md:text-lg">{t("Un devis qui part par e-mail une fois rentré au bureau est un devis qui attend. Et un devis qui attend, c'est un client qui compare.")}</h2>
          <div className="mt-7 grid gap-4 sm:grid-cols-3">
            {HOOK.map(([Icon, title, d], i) => (
              <div key={title} data-testid={`demo-hook-${i}`} className={`rounded-2xl border p-5 ${i === 2 ? "border-[#3A5CC8] bg-[#3A5CC8]/15" : "border-white/10 bg-white/[0.04]"}`}>
                <Icon className={`h-5 w-5 ${i === 2 ? "text-[#9FB4F2]" : "text-white/60"}`} />
                <p className="mt-3 font-heading text-sm font-bold">{t(title)}</p>
                <p className="mt-1 text-sm leading-relaxed text-white/70">{t(d)}</p>
              </div>
            ))}
          </div>
        </section>

        <h2 className="mt-14 font-heading text-base font-bold text-[#0F2645] md:text-lg">{t("Les 9 étapes montrées dans la vidéo")}</h2>
        <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {STEPS.map(([Icon, title, d], i) => (
            <div key={title} data-testid={`demo-step-${i + 1}`} className="dv-card-in rounded-2xl border border-[#E2E7F7] bg-white p-5 shadow-[0_1px_2px_rgba(15,38,69,0.04)]" style={{ animationDelay: `${i * 60}ms` }}>
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#08104D] text-[#9FB4F2]"><Icon className="h-4 w-4" /></span>
              <p className="mt-3 font-heading text-base font-bold text-[#0F2645]">{i + 1}. {t(title)}</p>
              <p className="mt-1 text-sm leading-relaxed text-[#5C6A87]">{t(d)}</p>
            </div>
          ))}
        </div>

        <DemoVertical />

        <div className="mt-12 flex flex-wrap gap-3">
          <Link to="/studio/inscription" data-testid="demo-cta" className="inline-flex items-center gap-2 rounded-xl bg-[#3A5CC8] px-7 py-4 text-sm font-bold text-white shadow-[0_6px_16px_-8px_rgba(58,92,200,0.5)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#3B5ECB]">
            {t("Essayer gratuitement 30 jours")} <ArrowRight className="h-4 w-4" />
          </Link>
          <a href={demoFile("devisto-demo", lang, ".mp4")} download data-testid="demo-download" className="inline-flex items-center gap-2 rounded-xl border border-[#E2E7F7] bg-white px-7 py-4 text-sm font-semibold text-[#0F2645] hover:border-[#3A5CC8]">{t("Télécharger la vidéo (MP4)")}</a>
        </div>
      </main>
      <Footer />
    </div>
  );
}
